// ABOUTME: Forgot password page component where users can enter their email to receive a reset link
// ABOUTME: Sends password reset request to API and displays appropriate success/error messages
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Avatar,
  Card,
  CardContent
} from '@mui/material';
import { Email as EmailIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { forgotPassword } from '../services/api';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await forgotPassword(email);
      setMessage(response.message || "If that email exists, we've sent you a reset link.");
      setIsSubmitted(true);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 500) {
        setError('Failed to send reset email. Please try again.');
      } else {
        const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
        setError(message || 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear errors when user starts typing
    if (error) {
      setError('');
    }
  };

  if (isSubmitted) {
    return (
      <Container 
        maxWidth="sm" 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          py: 3,
          background: theme => theme.palette.gradient.secondary,
        }}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center">
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                bgcolor: 'success.main',
                color: 'success.contrastText',
                mx: 'auto',
                mb: 2
              }}
            >
              <EmailIcon fontSize="large" />
            </Avatar>
            
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Check Your Email
            </Typography>
            
            <Card sx={{ mt: 3, mb: 4 }}>
              <CardContent>
                <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                  {message}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  If you don&apos;t see the email in your inbox, please check your spam folder.
                  The reset link will expire in 15 minutes for security.
                </Typography>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/login')}
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        py: 3,
        background: theme => theme.palette.gradient.secondary,
      }}
    >
      <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Avatar 
            sx={{ 
              width: 64, 
              height: 64, 
              bgcolor: 'primary.main', 
              mx: 'auto', 
              mb: 2 
            }}
          >
            <EmailIcon fontSize="large" />
          </Avatar>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Forgot Password?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </Typography>
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            required
            variant="outlined"
            margin="normal"
            error={!!error && !isValidEmail(email)}
            helperText={
              error && !isValidEmail(email) 
                ? "Please enter a valid email address"
                : "We'll send reset instructions to this email"
            }
            autoFocus
          />

          {error && isValidEmail(email) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!email || !isValidEmail(email) || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <EmailIcon />}
            sx={{ mt: 3, mb: 2 }}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </Box>

        {/* Back to Login */}
        <Box textAlign="center" mt={3}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/login')}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              textDecoration: 'none', 
              '&:hover': { textDecoration: 'underline' } 
            }}
          >
            <ArrowBackIcon fontSize="small" />
            Back to Login
          </Link>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;